// Central config for Planet HypeTunes world client.

// The shared HypeTunes API listens on PORT 3001 in dev (see API .env PORT=3001);
// port 3000 is the web front-end, not the API.
//
// Phone/LAN dev fix: when the page is served from a private LAN IP (a phone
// hitting the dev machine, e.g. 10.0.0.217:5174) but the API base points at
// localhost, that localhost is the PHONE's — so every proxied asset + API call
// 404s. Rewrite to the same LAN host:3001. Explicit non-localhost VITE_API_BASE
// (prod: api.hypetunes.com) is always respected.
const LAN_RE = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;
function resolveBase(envVal) {
  const loc = typeof window !== 'undefined' ? window.location.hostname : '';
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  // HTTPS dev (mic-secure-context, see vite.config.js): call the API SAME-ORIGIN
  // ('') so Vite's proxy forwards it — a direct http://…:3001 fetch from an
  // https page is mixed-content-blocked. Prod (explicit https api host) is left
  // untouched: its envVal isn't localhost/private, so it passes straight through.
  if (isHttps && (!envVal || /localhost|127\.0\.0\.1|:\/\/(10\.|192\.168\.|172\.)/.test(envVal))) {
    return '';
  }
  const onLan = LAN_RE.test(loc);
  if (onLan && (!envVal || /localhost|127\.0\.0\.1/.test(envVal))) {
    return `${window.location.protocol}//${loc}:3001`;
  }
  return envVal || 'http://localhost:3001';
}
// Phones have a far tighter GPU/memory ceiling than desktop — used to gate the
// heaviest loads (Gaussian-splat rooms, retina dpr, shadows/MSAA) so iOS Safari
// doesn't OOM-crash ("a problem repeatedly occurred"). Single source of truth.
export const IS_MOBILE = typeof navigator !== 'undefined'
  && (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    || (typeof window !== 'undefined' && 'ontouchstart' in window && Math.min(window.innerWidth, window.innerHeight) < 820));

export const API_BASE = resolveBase(import.meta.env.VITE_API_BASE).replace(/\/$/, '');
export const SOCKET_BASE = resolveBase(import.meta.env.VITE_SOCKET_BASE || import.meta.env.VITE_API_BASE).replace(/\/$/, '');
// Auth for billed features (live video chat). Sources, in order:
// VITE_AUTH_TOKEN (.env) → ?token=<jwt> in the URL (saved locally, so it only
// has to be pasted once) → previously saved token.
const qsToken = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('token') : null;
if (qsToken) { try { localStorage.setItem('world:authToken', qsToken); } catch { /* private mode */ } }
let savedToken = '';
try { savedToken = localStorage.getItem('world:authToken') || ''; } catch { /* private mode */ }
// Tolerate the common paste shapes: surrounding quotes and a copied
// "Bearer " prefix (we add Bearer ourselves on every request).
const cleanToken = (t) => String(t || '').trim().replace(/^["']|["']$/g, '').replace(/^Bearer\s+/i, '');
export const AUTH_TOKEN = cleanToken(import.meta.env.VITE_AUTH_TOKEN || qsToken || savedToken);

// Decode the JWT payload (client-side, unverified — for UI gating only, never
// trust for anything the server must enforce). → { userId, isAdmin }.
// isAdmin: the token's own claim, or the owner account (userId 1) as fallback.
// ?asguest=1 forces non-admin so the gate can be tested from an admin login.
export function authUser() {
  try {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('asguest')) {
      return { userId: null, isAdmin: false };
    }
    const payload = (AUTH_TOKEN || '').split('.')[1];
    const p = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return { userId: p.userId ?? null, isAdmin: p.isAdmin === true || p.userId === 1 };
  } catch { return { userId: null, isAdmin: false }; }
}

// The slice: Hip-Hop Town — Oakland, CA. The town lives on Oakland local time
// (see src/worldTime.js); golden hour is its hero look, pinnable for demos
// via ?time=golden.
export const SLICE_DISTRICT = {
  id: 'hiphop-town',
  name: 'Hip-Hop Town',
  city: 'Oakland',
  timezone: 'America/Los_Angeles',
  stationSlug: 'hiphop', // Hip Hop Radio — the genre station powering this district
  palette: {
    sky: '#ff9a3c',
    horizon: '#ffd27d',
    fog: '#e8935a',
    sun: '#ffb25e',
    asphalt: '#2b2b30',
    neon: '#ff5e78'
  }
};